import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getAuth } from "firebase/auth";
import { FaPlus, FaUndo } from "react-icons/fa";
import {
  asignarPropiedadesASocio,
  liberarPropiedadDeSocio,
  listarPropiedadesDeSocio,
  listarPropiedadesDisponibles,
  obtenerSocio,
} from "../../services/sociosService";
import notify from "../common/notify";
import { dialogService } from "../common/dialogService";
import "./Socios.css";
import { toJsDate } from "../../helpers/fechas";

const SocioDetalle = () => {
  const { socioId } = useParams();
  const navigate = useNavigate();
  const [socio, setSocio] = useState(null);
  const [propsSocio, setPropsSocio] = useState([]);
  const [propsDisponibles, setPropsDisponibles] = useState([]);
  const [seleccionadas, setSeleccionadas] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mostrarAsignar, setMostrarAsignar] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [s, ps, pd] = await Promise.all([
        obtenerSocio(socioId),
        listarPropiedadesDeSocio(socioId),
        listarPropiedadesDisponibles(),
      ]);
      if (!s) throw new Error("Socio no encontrado");
      setSocio(s);
      setPropsSocio(ps);
      setPropsDisponibles(pd);
    } catch (e) {
      console.error(e);
      notify.error(e?.message || "No se pudo cargar el socio");
      navigate("/app/socios");
    } finally {
      setLoading(false);
    }
  }, [navigate, socioId]);

  useEffect(() => { cargar(); }, [cargar]);

  const activas = useMemo(
    () => propsSocio.filter((p) => p.apartadaSocio || p.estado === "apartada_socio"),
    [propsSocio]
  );

  const disponiblesFiltradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return propsDisponibles.filter((p) => {
      const texto = `${p.folio || ""} ${p.nombre || ""} ${p.fraccNombre || ""} ${p.manzana || ""} ${p.lote || ""}`.toLowerCase();
      return texto.includes(q);
    });
  }, [propsDisponibles, busqueda]);

  const toggle = (id) => {
    setSeleccionadas((arr) => arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const asignar = async () => {
    if (!seleccionadas.length) { notify.error("Selecciona al menos una propiedad"); return; }
    setSaving(true);
    try {
      const user = getAuth().currentUser;
      await asignarPropiedadesASocio({ socioId, propiedadIds: seleccionadas, usuario: user });
      setSeleccionadas([]);
      setMostrarAsignar(false);
      await cargar();
      notify.success("Propiedades apartadas correctamente");
    } catch (e) {
      console.error(e);
      notify.error("No se pudo asignar: " + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const liberar = async (prop) => {
    const motivo = await dialogService.prompt(`Motivo para liberar/regresar ${prop.folio || prop.nombre || "la propiedad"}:`, "Regresada por socio");
    if (motivo === null) return;
    setSaving(true);
    try {
      const user = getAuth().currentUser;
      await liberarPropiedadDeSocio({ propiedadId: prop.id, motivo, usuario: user });
      await cargar();
      notify.success("Propiedad liberada correctamente");
    } catch (e) {
      console.error(e);
      notify.error("No se pudo liberar: " + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  const money = (n) => Number(n || 0).toLocaleString();

  if (loading) return <div className="socios-page"><div className="socios-card">Cargando socio…</div></div>;
  if (!socio) return null;

  return (
    <div className="socios-page">
      <div className="socios-card">
        <div className="socios-toolbar">
          <button className="socios-btn secondary" onClick={() => navigate("/app/socios")}>← Socios</button>
          <div className="socios-actions">
            <Link className="socios-btn secondary" to={`/app/socios/${socioId}/editar`}>Editar socio</Link>
            <button className="socios-btn" onClick={() => setMostrarAsignar((v) => !v)}><FaPlus /> Asignar terrenos</button>
          </div>
        </div>

        <h2 className="socios-title">{socio.nombre} {socio.apellidos}</h2>
        <div className="socios-grid">
          <Field label="Teléfono" value={socio.telefono} />
          <Field label="Correo" value={socio.email} />
          <Field label="Dirección" value={socio.direccion} wide />
          <Field label="Notas" value={socio.notas} wide />
          <Field label="Estado" value={socio.activo === false ? "Inactivo" : "Activo"} />
          <Field label="Propiedades apartadas" value={activas.length} />
        </div>
      </div>

      {mostrarAsignar && (
        <div className="socios-card">
          <h3>Asignar propiedades disponibles</h3>
          <div className="socios-toolbar">
            <input className="socios-input" placeholder="Buscar por folio, nombre, manzana, lote o fraccionamiento" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            <button className="socios-btn" disabled={saving || !seleccionadas.length} onClick={asignar}>Apartar seleccionadas ({seleccionadas.length})</button>
          </div>
          <div className="socios-prop-picker">
            {disponiblesFiltradas.map((p) => (
              <label key={p.id} className="socios-prop-item">
                <input type="checkbox" checked={seleccionadas.includes(p.id)} onChange={() => toggle(p.id)} />
                <div>
                  <strong>{p.folio || "Sin folio"}</strong> — {p.nombre || "Propiedad"}
                  <div className="socios-muted">{p.fraccNombre || p.fraccionamientoId || "Sin fraccionamiento"} · MZ {p.manzana || "-"} LT {p.lote || "-"}</div>
                  <div className="socios-muted">${money(p.precioContado ?? p.precio)}</div>
                </div>
              </label>
            ))}
            {!disponiblesFiltradas.length && <div className="socios-muted">No hay propiedades disponibles con ese filtro.</div>}
          </div>
        </div>
      )}

      <div className="socios-card">
        <h3>Propiedades actualmente apartadas</h3>
        <div className="socios-table-wrap">
          <table className="socios-table">
            <thead>
              <tr><th>Folio</th><th>Nombre</th><th>Fraccionamiento</th><th>MZ/LT</th><th>Precio</th><th>Fecha apartado</th><th>Acciones</th></tr>
            </thead>
            <tbody>
              {activas.map((p) => (
                <tr key={p.id}>
                  <td>{p.folio || "-"}</td>
                  <td>{p.nombre || "-"}</td>
                  <td>{p.fraccNombre || p.fraccionamientoId || "-"}</td>
                  <td>MZ {p.manzana || "-"} / LT {p.lote || "-"}</td>
                  <td>${money(p.precioContado ?? p.precio)}</td>
                  <td>{toJsDate(p.fechaApartadoSocio)?.toLocaleDateString() || "-"}</td>
                  <td className="socios-actions">
                    <Link className="socios-btn secondary" to={`/app/propiedades/${p.id}`}>Ver</Link>
                    <button className="socios-btn danger" disabled={saving} onClick={() => liberar(p)}><FaUndo /> Liberar</button>
                  </td>
                </tr>
              ))}
              {!activas.length && <tr><td colSpan="7" style={{ textAlign: "center", opacity: .7 }}>Este socio no tiene propiedades apartadas.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, wide }) => (
  <div className={`socios-field ${wide ? "wide" : ""}`}>
    <div className="socios-label">{label}</div>
    <div className="socios-value">{value || "-"}</div>
  </div>
);

export default SocioDetalle;
